/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

export const VersionStatusSchema = z.enum(['building', 'built', 'build_failed']);

/** How far delivery has got on the device; `sent` means the command was published and nothing has answered yet. */
export const DeploymentStateSchema = z.enum(['sent', 'downloading', 'applied', 'rolled_back', 'failed']);
export type DeploymentState = z.infer<typeof DeploymentStateSchema>;

/**
 * One build of the app. Every attempt gets a build number, which is how the build is stored, built and found again;
 * only a build that succeeds gets a version number, the one the user sees and the device runs. So a failed build
 * never uses up a version.
 */
export const VersionSchema = z.object({
  project_id: z.string().uuid(),
  /** Counts every build of the project, failed ones included. */
  build: z.number().int().positive(),
  /** Counts the builds that succeeded; null until this one has. */
  version: z.number().int().positive().nullable().default(null),
  device_id: z.string().uuid(),
  status: VersionStatusSchema,
  deployment_state: DeploymentStateSchema.nullable().default(null),
  error: z.string().nullable().default(null),
  artifact_size: z.number().int().nonnegative().nullable().default(null),
  /** What this version changes, as the assistant or the user described it. */
  summary: z.string().nullable().default(null),
  /** The build whose source this one was built on; null when built on the app template. */
  base_build: z.number().int().positive().nullable().default(null),
  /** The end of the build log of a failed build: compiler errors, failing tests. */
  log_tail: z.string().nullable().default(null),
  /** Which check a failed build fell at: a jit-verify step (frontend_types, modules, go_vet, go_test) or `build`. */
  failed_gate: z.string().nullable().default(null),
  /** What the assistant used to produce this version (planning and building of its request); null when unknown. */
  input_tokens: z.number().int().nonnegative().nullable().default(null),
  output_tokens: z.number().int().nonnegative().nullable().default(null),
  /** How long the assistant worked on the request (planning and building, not the wait for approval). */
  elapsed_ms: z.number().int().nonnegative().nullable().default(null),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Version = z.infer<typeof VersionSchema>;

export const VersionDownloadKindSchema = z.enum(['binary', 'source']);
export type VersionDownloadKind = z.infer<typeof VersionDownloadKindSchema>;

export const VersionDownloadQuerySchema = z.object({ kind: VersionDownloadKindSchema }).strict();

/** A short-lived link to a zip of the version: its binary with a README, or its source code. */
export const VersionDownloadResponseSchema = z.object({
  url: z.string().url(),
  filename: z.string().min(1),
});
export type VersionDownloadResponse = z.infer<typeof VersionDownloadResponseSchema>;

/**
 * Source overrides applied on top of the app template, path (relative to the template) to file content.
 * Only the app's own Go files and its frontend can be replaced; dependencies and build files cannot.
 */
export const SourceFilesSchema = z.record(z.string(), z.string());

export const CreateVersionRequestSchema = z
  .object({
    device_id: z.string().uuid(),
    files: SourceFilesSchema.default({}),
    /** Write the files over this build's source instead of the template. */
    base_build: z.number().int().positive().optional(),
    /** False builds and verifies only; the binary is sent later with a deploy request. */
    deliver: z.boolean().default(true),
    summary: z.string().trim().max(500).optional(),
  })
  .strict();
export type CreateVersionRequest = z.input<typeof CreateVersionRequestSchema>;

export const ListVersionsResponseSchema = z.object({ versions: z.array(VersionSchema) });
export type ListVersionsResponse = z.infer<typeof ListVersionsResponseSchema>;

/** What the device reports on `jit/{device_id}/deployments`, as the IoT rule forwards it. */
export const DeploymentReportEventSchema = z.object({
  deployment_id: z.string().min(1).max(100),
  state: z.enum(['downloading', 'applied', 'rolled_back', 'failed']),
  error: z.string().max(2000).optional(),
  ts: z.number().int().positive(),
  device_id: z.string().uuid(),
});
